/**
 * Demo Test Component
 * 
 * Simple test component to verify demo functionality.
 */

import React, { useState } from 'react';
import { DemoAPI } from '../../services/demo-api';
import './DemoTest.css';

export const DemoTest: React.FC = () => {
  const [status, setStatus] = useState<string>('Ready to test');
  const [loading, setLoading] = useState(false);
  const [sessionKey, setSessionKey] = useState<string>('');

  const testDemoAPI = async () => {
    setLoading(true);
    setStatus('Testing demo API...');

    try {
      // Test health check
      const health = await DemoAPI.healthCheck();
      setStatus(`Health check passed: ${JSON.stringify(health)}`);

      // Create a demo session
      const session = await DemoAPI.createSession();
      setSessionKey(session.session_key);
      setStatus(`Demo session created: ${session.session_key}`);

    } catch (error) {
      console.error('Demo API test failed:', error);
      setStatus(`Demo API test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testUserInput = async () => {
    if (!sessionKey) {
      setStatus('Please create a session first');
      return;
    }

    setLoading(true);
    setStatus('Testing user input...');

    try {
      const result = await DemoAPI.processUserInput(sessionKey, 'Hello, let\'s start the demo!');
      setStatus(`User input processed: ${JSON.stringify(result.response.message)}`);
    } catch (error) {
      console.error('User input test failed:', error);
      setStatus(`User input test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const testCardAction = async () => {
    if (!sessionKey) {
      setStatus('Please create a session first');
      return;
    }

    setLoading(true);
    setStatus('Testing card action...');

    try {
      const result = await DemoAPI.handleCardAction(sessionKey, 'create-company', {
        company_name: 'Test Company',
        industry: 'technology'
      });
      setStatus(`Card action handled: ${JSON.stringify(result.action_result)}`);
    } catch (error) {
      console.error('Card action test failed:', error);
      setStatus(`Card action test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="demo-test">
      <h2>Demo System Test</h2>
      
      <div className="test-controls">
        <button 
          onClick={testDemoAPI}
          disabled={loading}
          className="test-button primary"
        >
          {loading ? 'Testing...' : 'Test Demo API'}
        </button>

        <button 
          onClick={testUserInput}
          disabled={loading || !sessionKey}
          className="test-button secondary"
        >
          Test User Input
        </button>

        <button 
          onClick={testCardAction}
          disabled={loading || !sessionKey}
          className="test-button secondary"
        >
          Test Card Action
        </button>
      </div>

      <div className="test-status">
        <h3>Status:</h3>
        <p>{status}</p>
        {sessionKey && (
          <p><strong>Session Key:</strong> {sessionKey}</p>
        )}
      </div>

      <div className="test-info">
        <h3>Test Information:</h3>
        <ul>
          <li>This component tests the demo API endpoints</li>
          <li>It creates a demo session and tests basic functionality</li>
          <li>Check the browser console for detailed logs</li>
          <li>Make sure the backend is running on the correct port</li>
        </ul>
      </div>
    </div>
  );
};

export default DemoTest;
